/**
 * useNavCounts — honest-or-absent sidebar count badges (Story UI-U2 AC 3).
 *
 * Live mode: cheap `limit: 1` probe queries that FEATURE-DETECT a numeric
 * total on the response envelope. Today NO list endpoint returns a total
 * (`GET /tasks` = { data, page: { next_cursor, limit } }; `GET /invoices` =
 * { items }) — the parallel backend wave is adding pagination meta. A missing
 * field, failed request, or missing endpoint → NO badge (never `0`, never a
 * guess, no error surfaced). Mock mode: deterministic fixture counts.
 *
 * Sources: kommunikation = open tasks · aufgaben = the caller's group queue ·
 * rechnungen = in_review + blocked invoice probes (sum; if EITHER lacks a
 * total → key omitted) · courtage = none yet (no endpoint → no badge) ·
 * posteingang = the Eingangsliste's OWN count hook, merged in here rather than
 * re-probed: `usePosteingangCounts` is the single owner of that number (mock
 * today, `GET /posteingang/counts` when B1 step 3 lands), and duplicating its
 * rule in this module would make the badge and the list disagree.
 *
 * [Source: apps/web/src/features/dashboard/hooks/useHeuteRenewals.ts] (mock/live pattern)
 * [Source: docs/stories/ui-u2-ia-navigation.md AC 3 + Dev Notes readTotal contract]
 */

import { useQuery } from '@tanstack/react-query';
import type { TaskStatus } from '@brokly/shared';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { apiClient } from '../../lib/api-client';
import { usePosteingangCounts } from '../../features/posteingang/hooks/usePosteingang';
import type { BadgeKey } from './nav';

export type NavCounts = Partial<Record<BadgeKey, number>>;

/** Deterministic mock-mode counts (matches the prototype's demo feel). */
export const NAV_COUNT_FIXTURE: NavCounts = {
  kommunikation: 5,
  aufgaben: 3,
  rechnungen: 8,
};

/** task.status probe value — from the shared TaskStatus union (no magic values). */
const TASK_STATUS_OPEN: TaskStatus = 'open';

/**
 * Accepts any list envelope; returns a count ONLY when the server sent one.
 * Correct BOTH before and after the backend wave's pagination meta lands:
 * looks for `page.total` first, then a top-level `total`; anything non-numeric
 * (absent field, error body, old envelope) → null → no badge.
 */
export function readTotal(res: unknown): number | null {
  const r = res as { page?: { total?: unknown }; total?: unknown } | null | undefined;
  const t = r?.page?.total ?? r?.total;
  return typeof t === 'number' && Number.isFinite(t) ? t : null;
}

/** Total of a settled probe — rejected probe counts as "no total" (no badge). */
function settledTotal(result: PromiseSettledResult<unknown>): number | null {
  return result.status === 'fulfilled' ? readTotal(result.value) : null;
}

/** Fire all probes in ONE queryFn; absent key = no badge (AC 3). */
async function fetchLiveCounts(): Promise<NavCounts> {
  const [tasksOpen, groupQueue, invoicesInReview, invoicesBlocked] = await Promise.allSettled([
    apiClient.tasks.list({ status: TASK_STATUS_OPEN, limit: 1 }),
    apiClient.tasks.queue({ limit: 1 }),
    apiClient.invoices.list({ status: 'in_review', limit: 1 }),
    apiClient.invoices.list({ status: 'blocked', limit: 1 }),
  ]);

  const counts: NavCounts = {};

  const kommunikation = settledTotal(tasksOpen);
  if (kommunikation !== null) counts.kommunikation = kommunikation;

  const aufgaben = settledTotal(groupQueue);
  if (aufgaben !== null) counts.aufgaben = aufgaben;

  const inReview = settledTotal(invoicesInReview);
  const blocked = settledTotal(invoicesBlocked);
  if (inReview !== null && blocked !== null) counts.rechnungen = inReview + blocked;

  return counts;
}

/**
 * The nav-count map for the sidebar. Absent key = render no badge.
 * Single query (one object result); never throws into the UI.
 */
export function useNavCounts(): NavCounts {
  const live = isLiveData();
  // Posteingang owns its own count (see the header) — merged, never re-derived.
  const { data: posteingang } = usePosteingangCounts();
  const { data } = useQuery<NavCounts>({
    queryKey: qk.navCounts(),
    queryFn: () => (live ? fetchLiveCounts() : Promise.resolve(NAV_COUNT_FIXTURE)),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    // mock mode: synchronous initialData (no flicker, deterministic tests)
    ...(live ? {} : { initialData: NAV_COUNT_FIXTURE }),
  });
  return {
    ...(data ?? {}),
    // The sidebar badges the OPEN count; `unread` is the Eingangsliste's own badge.
    ...(typeof posteingang?.offen === 'number' ? { posteingang: posteingang.offen } : {}),
  };
}
